/**
 * Данные вкладки «Статистика»: типы, отбор и порядок.
 *
 * Вынесено из страницы: условий отбора стало столько, что проверять их
 * глазами по разметке нельзя, а тестами — можно. Разметка остаётся про вид.
 */

/** Строка сводки по игроку: всё, что известно про него по каптам. */
export interface StatsPlayer {
  staticId: string;
  captures: number;
  damage: number;
  kills: number;
  wins: number;
  losses: number;
  lastCaptureAt: string;
  /** Имя из ответа службы — запасное, когда анкеты нет. */
  captureName: string;
  userId: string | null;
  gameNick: string | null;
  /** Имя в Discord из анкеты. `null` — анкеты нет либо бот его не видел. */
  discordName: string | null;
  /** Аватарка из Discord. `null` — показываем знак с буквой. */
  avatarUrl: string | null;
  tier: number | null;
  /** Каптов за последние семь дней. */
  capturesWeek: number;
  /** Каптов за последние тридцать дней. */
  capturesMonth: number;
  /**
   * Цифры, которые есть только у vzp-gta5rp.com. `null` — сайт игрока ещё не
   * прислал; тогда МП, убийства и урон выше — из наших каптов.
   */
  deaths?: number | null;
  kd?: number | null;
  hsPercent?: number | null;
  hitPercent?: number | null;
}

/** Выбранный сервер статистики запоминается в браузере: заходят обычно за одним и тем же. */
const SERVER_KEY = 'ak-stats-server';

export function readSavedServer(): string {
  try {
    return localStorage.getItem(SERVER_KEY) ?? '';
  } catch {
    return '';
  }
}

export function saveServer(server: string): void {
  try {
    localStorage.setItem(SERVER_KEY, server);
  } catch {
    // Без хранилища выбор просто не переживёт перезагрузку.
  }
}

/** K/D строкой: с сайта, а без него — прочерк (из наших каптов его не посчитать). */
export function formatKd(player: { kd?: number | null }): string {
  return player.kd === null || player.kd === undefined ? '—' : player.kd.toFixed(2);
}

/** Бой в ленте: сам капт и сводка по нашей стороне. */
export interface StatsCapture {
  captureId: number;
  familyId: number;
  startedAt: string;
  gangZoneId: number | null;
  defendersId: number;
  attackersId: number;
  defendersName: string | null;
  attackersName: string | null;
  winner: number | null;
  players: number;
  damage: number;
  kills: number;
}

export interface FamilySummary {
  id: number | null;
  name: string | null;
}

/**
 * Сколько каптов было у самой семьи за окна.
 *
 * Знаменатель к цифрам игрока: «четыре капта за месяц» ничего не говорит,
 * пока неизвестно, было их всего пять или сорок.
 */
export interface CaptureActivity {
  week: number;
  month: number;
}

/** Как зовут человека: игровой ник из анкеты, иначе имя из ответа службы. */
export function playerName(player: StatsPlayer): string {
  return player.gameNick ?? player.captureName;
}

/** Итог капта нашими глазами: победа, поражение или неизвестно. */
export function captureOutcome(capture: { winner: number | null; familyId: number }): 'win' | 'loss' | 'unknown' {
  if (capture.winner === null) return 'unknown';
  return capture.winner === capture.familyId ? 'win' : 'loss';
}

/** Подписи исхода боя — одни на все списки. */
export const OUTCOME_LABEL: Readonly<Record<'win' | 'loss' | 'unknown', string>> = {
  win: 'Победа',
  loss: 'Поражение',
  unknown: 'Итог неизвестен',
};

/**
 * Цвет исхода. Один на все места, где исход показывают.
 *
 * `--positive`, а не `--success`: последнего в теме нет и никогда не было, и
 * из-за опечатки победа однажды рисовалась бесцветной.
 */
export function outcomeColor(outcome: 'win' | 'loss' | 'unknown'): string {
  return outcome === 'win' ? 'var(--positive)' : outcome === 'loss' ? 'var(--danger)' : 'var(--text-muted)';
}

/** Кто был противником: вторая сторона боя. */
export function opponentName(capture: StatsCapture): string {
  const weDefended = capture.defendersId === capture.familyId;
  const name = weDefended ? capture.attackersName : capture.defendersName;
  const id = weDefended ? capture.attackersId : capture.defendersId;

  return name ?? (id > 0 ? `семья ${String(id)}` : 'нейтральная зона');
}

/**
 * Доля побед. `null` — все капты человека без отмеченного итога.
 *
 * Капты, по которым служба не сказала победителя, в знаменатель не идут:
 * иначе доля падала бы просто оттого, что данных нет.
 */
export function winRate(player: StatsPlayer): number | null {
  const decided = player.wins + player.losses;
  return decided === 0 ? null : player.wins / decided;
}

function perCapture(player: StatsPlayer, total: number): number {
  return player.captures === 0 ? 0 : total / player.captures;
}

export type PlayerSortKey =
  | 'damage'
  | 'avgDamage'
  | 'kills'
  | 'avgKills'
  | 'kd'
  | 'captures'
  | 'wins'
  | 'winRate'
  | 'lastCaptureAt'
  | 'nick';

/** Подписи для выбора порядка — они же задают его состав. */
export const PLAYER_SORTS: { key: PlayerSortKey; label: string }[] = [
  { key: 'damage', label: 'Урону' },
  { key: 'avgDamage', label: 'Урону за капт' },
  { key: 'kills', label: 'Киллам' },
  { key: 'avgKills', label: 'Киллам за капт' },
  { key: 'kd', label: 'K/D' },
  { key: 'captures', label: 'Числу МП' },
  { key: 'winRate', label: 'Доле побед' },
  { key: 'lastCaptureAt', label: 'Последнему капту' },
  { key: 'nick', label: 'Нику' },
];

export function comparePlayers(
  left: StatsPlayer,
  right: StatsPlayer,
  key: PlayerSortKey,
  descending: boolean,
): number {
  const sign = descending ? -1 : 1;

  switch (key) {
    case 'captures':
      return sign * (left.captures - right.captures);
    case 'damage':
      return sign * (left.damage - right.damage);
    case 'avgDamage':
      return sign * (perCapture(left, left.damage) - perCapture(right, right.damage));
    case 'kills':
      return sign * (left.kills - right.kills);
    case 'avgKills':
      return sign * (perCapture(left, left.kills) - perCapture(right, right.kills));
    case 'wins':
      return sign * (left.wins - right.wins);
    case 'kd':
      return sign * ((left.kd ?? -1) - (right.kd ?? -1));
    case 'winRate': {
      // Без единого решённого капта доли нет: такой игрок уходит вниз в обе
      // стороны, как и всякий, о ком сравнивать нечего.
      const leftRate = winRate(left);
      const rightRate = winRate(right);
      if (leftRate === null || rightRate === null) {
        return leftRate === rightRate ? 0 : leftRate === null ? 1 : -1;
      }

      return sign * (leftRate - rightRate);
    }
    case 'lastCaptureAt':
      return sign * (left.lastCaptureAt < right.lastCaptureAt ? -1 : left.lastCaptureAt > right.lastCaptureAt ? 1 : 0);
    default:
      return sign * playerName(left).localeCompare(playerName(right), 'ru');
  }
}

/** Давно ли человек выходил на капт. */
export type StatsActivity = 'all' | 'week' | 'month' | 'stale';

export interface StatsFilters {
  query: string;
  /** Пустой список — тир не ограничивает выборку. */
  tiers: number[];
  activity: StatsActivity;
  /** `null` — порога нет. */
  minCaptures: number | null;
  minAvgDamage: number | null;
  minAvgKills: number | null;
  /** Доля побед в процентах: 60 значит «не меньше 60 %». */
  minWinRate: number | null;
}

export const EMPTY_STATS_FILTERS: StatsFilters = {
  query: '',
  tiers: [],
  activity: 'all',
  minCaptures: null,
  minAvgDamage: null,
  minAvgKills: null,
  minWinRate: null,
};

/** Задан ли хоть один отбор. По нему же прячется пьедестал. */
export function isStatsFilterActive(filters: StatsFilters): boolean {
  return (
    filters.query.trim() !== '' ||
    filters.tiers.length > 0 ||
    filters.activity !== 'all' ||
    filters.minCaptures !== null ||
    filters.minAvgDamage !== null ||
    filters.minAvgKills !== null ||
    filters.minWinRate !== null
  );
}

function matchesActivity(player: StatsPlayer, activity: StatsActivity): boolean {
  switch (activity) {
    case 'week':
      return player.capturesWeek > 0;
    case 'month':
      return player.capturesMonth > 0;
    case 'stale':
      return player.capturesMonth === 0;
    default:
      return true;
  }
}

/**
 * Показывать ли человека в статистике вообще.
 *
 * Прячутся те, у кого нет ни анкеты, ни тира: случайные люди, разово
 * оказавшиеся в зоне вместе с нами, и бывшие свои, давно ушедшие. Витрина
 * должна отвечать на вопрос «как воюет семья», а не перечислять всех, кого
 * служба когда-либо присылала в составе.
 *
 * Это именно скрытие, а не удаление: капты и цифры остаются в базе. Тир
 * снимают и на пару часов — разбирают жалобу, переводят между составами, —
 * и человек обязан вернуться в списки сам, как только роль вернут. Ровно по
 * этой причине правило живёт здесь, в разметке, а не в запросе к базе.
 */
export function isShownInStats(player: StatsPlayer): boolean {
  return player.userId !== null || (player.tier !== null && player.tier !== 0);
}

export function filterStatsPlayers(players: readonly StatsPlayer[], filters: StatsFilters): StatsPlayer[] {
  const needle = filters.query.trim().toLowerCase();

  return players.filter((player) => {
    if (!isShownInStats(player)) {
      return false;
    }

    if (needle !== '') {
      /*
       * В поиск входит и Discord: в канале человека зовут по нику Discord,
       * а в составе капта он подписан игровым именем, и связать одно с
       * другим по памяти выходит не у всех. Идентификатор ищется наравне с
       * именем — его копируют из Discord, когда имя не помнят вовсе.
       */
      const haystack = [
        playerName(player),
        player.staticId,
        player.captureName,
        player.discordName ?? '',
        player.userId ?? '',
      ]
        .join(' ')
        .toLowerCase();

      if (!haystack.includes(needle)) {
        return false;
      }
    }

    if (filters.tiers.length > 0 && (player.tier === null || !filters.tiers.includes(player.tier))) {
      return false;
    }

    if (!matchesActivity(player, filters.activity)) {
      return false;
    }

    if (filters.minCaptures !== null && player.captures < filters.minCaptures) {
      return false;
    }

    if (filters.minAvgDamage !== null && perCapture(player, player.damage) < filters.minAvgDamage) {
      return false;
    }

    if (filters.minAvgKills !== null && perCapture(player, player.kills) < filters.minAvgKills) {
      return false;
    }

    if (filters.minWinRate !== null) {
      const rate = winRate(player);
      // Доли нет — порог не пройден: приписать нули тому, о ком служба
      // молчит, значило бы записать его в проигрывающих.
      if (rate === null || rate * 100 < filters.minWinRate) {
        return false;
      }
    }

    return true;
  });
}

/**
 * С какой стороны бились наши: защищали зону или отбивали.
 *
 * Служба называет обе стороны, и наша семья — одна из них. Если ни одна не
 * наша (бой третьей семьи попал в ленту), считаем это атакой: такие капты в
 * ленту не пишутся, но выдумывать защиту зоны, которой у нас не было, хуже.
 */
export function captureSide(capture: StatsCapture): 'defence' | 'attack' {
  return capture.defendersId === capture.familyId ? 'defence' : 'attack';
}

export const SIDE_LABEL: Readonly<Record<'defence' | 'attack', string>> = {
  defence: 'в защите',
  attack: 'в атаке',
};

/** Сводка по отобранным боям — то, что стоит над лентой. */
export interface CaptureSummary {
  total: number;
  wins: number;
  losses: number;
  /** Бои, по которым служба не сказала итог. */
  unknown: number;
  /** Доля побед среди решённых боёв. `null` — решённых нет вовсе. */
  winRate: number | null;
  damage: number;
  kills: number;
}

export function summarizeCaptures(captures: readonly StatsCapture[]): CaptureSummary {
  const summary: CaptureSummary = {
    total: captures.length,
    wins: 0,
    losses: 0,
    unknown: 0,
    winRate: null,
    damage: 0,
    kills: 0,
  };

  for (const capture of captures) {
    const outcome = captureOutcome(capture);
    if (outcome === 'win') summary.wins += 1;
    else if (outcome === 'loss') summary.losses += 1;
    else summary.unknown += 1;

    summary.damage += capture.damage;
    summary.kills += capture.kills;
  }

  // Бои без отмеченного итога в знаменатель не идут: иначе доля падала бы от
  // того, что служба промолчала.
  const decided = summary.wins + summary.losses;
  summary.winRate = decided === 0 ? null : summary.wins / decided;

  return summary;
}

/** Лента, разложенная по дням: заголовок дня и бои этого дня. */
export interface CaptureDay {
  day: string;
  captures: StatsCapture[];
}

/**
 * Раскладывает бои по дням, сохраняя порядок ленты.
 *
 * Подпись дня приходит готовой функцией: перевод в часовой пояс сообщества
 * живёт в разметке, и тащить его сюда значило бы завести второй способ
 * форматировать дату.
 */
export function groupCapturesByDay(
  captures: readonly StatsCapture[],
  dayOf: (capture: StatsCapture) => string,
): CaptureDay[] {
  const days: CaptureDay[] = [];

  for (const capture of captures) {
    const day = dayOf(capture);
    const last = days.at(-1);

    if (last !== undefined && last.day === day) {
      last.captures.push(capture);
    } else {
      days.push({ day, captures: [capture] });
    }
  }

  return days;
}

export type CaptureOutcomeFilter = 'all' | 'win' | 'loss' | 'unknown';

/** Сторона, за которую бились наши. `all` — любая. */
export type CaptureSideFilter = 'all' | 'defence' | 'attack';

export interface CaptureFilters {
  query: string;
  outcome: CaptureOutcomeFilter;
  side: CaptureSideFilter;
  /** Название противника. `null` — любой. */
  opponent: string | null;
}

export const EMPTY_CAPTURE_FILTERS: CaptureFilters = { query: '', outcome: 'all', side: 'all', opponent: null };

export function isCaptureFilterActive(filters: CaptureFilters): boolean {
  return (
    filters.query.trim() !== '' ||
    filters.outcome !== 'all' ||
    filters.side !== 'all' ||
    filters.opponent !== null
  );
}

/** Противники, которые уже встречались, — список для отбора. */
export function opponentOptions(captures: readonly StatsCapture[]): string[] {
  return [...new Set(captures.map(opponentName))].sort((left, right) => left.localeCompare(right, 'ru'));
}

export function filterCaptures(captures: readonly StatsCapture[], filters: CaptureFilters): StatsCapture[] {
  const needle = filters.query.trim().toLowerCase();

  return captures.filter((capture) => {
    if (filters.outcome !== 'all' && captureOutcome(capture) !== filters.outcome) {
      return false;
    }

    if (filters.side !== 'all' && captureSide(capture) !== filters.side) {
      return false;
    }

    if (filters.opponent !== null && opponentName(capture) !== filters.opponent) {
      return false;
    }

    if (needle === '') {
      return true;
    }

    // Номер капта ищется наравне с названием: по нему бой находят, когда
    // о нём зашла речь в Discord.
    return `${opponentName(capture)} ${String(capture.captureId)}`.toLowerCase().includes(needle);
  });
}
